import { jobTypes } from "../seed/job-preferences-data";

type JobPreferences = Record<string, any>;

interface PersonalInfo {
	emailAddress?: string;
	firstName?: string;
	middleName?: string;
	lastName?: string;
	phoneNumber?: string;
}

/**
 * Older accounts come back with openForWorkTypes as a comma-joined string instead of an array. Left
 * alone the buttons still light up — String.includes matches substrings — and saving echoes the
 * string back to an endpoint that validates it as an enum array, which rejects the whole request
 * with "each value in openForWorkTypes must be one of the following values". Coerce to an array and
 * drop anything the API would refuse.
 */
export function normalizeWorkTypes(value: unknown): string[] {
	const entries = Array.isArray(value) ? value : typeof value === "string" ? value.split(",") : [];

	return entries.map((entry) => String(entry).trim()).filter((entry) => jobTypes.includes(entry));
}

/**
 * The full account payload PUT /users/account expects.
 *
 * It is whitelist-strict and replaces the whole preferences object, so a caller changing one field
 * still has to send every other one — which is why this lives here rather than inside the
 * preferences modal: the nav status badge writes through the same builder.
 */
export function toJobPreferencesRequestBody(currentPayload: JobPreferences = {}, personalInfo: PersonalInfo = {}) {
	return {
		emailAddress: personalInfo.emailAddress,
		firstName: personalInfo.firstName,
		middleName: personalInfo.middleName,
		lastName: personalInfo.lastName,
		phoneNumber: personalInfo.phoneNumber,
		jobPreferences: {
			primaryResumeReferenceId: currentPayload.primaryResumeReferenceId,
			currentJobsearchStatus: currentPayload.currentJobsearchStatus,
			openForWorkTypes: normalizeWorkTypes(currentPayload.openForWorkTypes),
			areasOfExpertise: currentPayload.areasOfExpertise,
			seniorityLevel: currentPayload.seniorityLevel,
			locationPreferenceType: currentPayload.locationPreferenceType,
			locationsPreferred: currentPayload.locationsPreferred,
			recentGraduate: currentPayload.recentGraduate,
			graduationDate: {
				month: currentPayload.graduationDate?.month,
				year: currentPayload.graduationDate?.year,
			},
			professionalSince: {
				month: currentPayload.professionalSince?.month,
				year: currentPayload.professionalSince?.year,
			},
			educationLevels: currentPayload.educationLevels,
			desiredTitle: currentPayload.desiredTitle,
			address: {
				city: currentPayload?.address?.city,
				// Kept alongside the city: the location picker stores "District,Region", so dropping the
				// region would leave the saved value unable to match an option on the way back in.
				region: currentPayload?.address?.region,
				country: "Tanzania",
			},
			skills: currentPayload.skills,
		},
	};
}
